import type { DefaultOptionType } from 'antd/es/select'
import { TriangleAlertIcon, XIcon } from 'lucide-react'
import React from 'react'

import { QuestionnaireTemplate } from '@/gen/questionnaire/v1/template_service_pb'
import { Questionnaire } from '@/gen/questionnaire/v2/model_pb'

import { Select } from '@/components/lemmala-design-system/select/select'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'

type AddQuestionnaireProps = {
  selectedTemplates: QuestionnaireTemplate[]
  setSelectedTemplates: (templates: QuestionnaireTemplate[]) => void
  unselectableTemplates: QuestionnaireTemplate[]
  questionnaires: Questionnaire[]
}

export const AddQuestionnaire = ({
  selectedTemplates,
  setSelectedTemplates,
  unselectableTemplates,
  questionnaires,
}: AddQuestionnaireProps) => {
  return (
    <div className='mt-4 flex flex-col gap-4'>
      <h3 className='text-main-foreground-primary'>Questionnaire(s)</h3>
      <div className='flex flex-col gap-2'>
        <Select<string>
          size='large'
          value={null}
          showSearch
          placeholder='Select questionnaire templates'
          onChange={(_, option: DefaultOptionType | undefined) => {
            if (option && 'template' in option && option.template) {
              setSelectedTemplates([...selectedTemplates, option.template])
            }
          }}
          options={unselectableTemplates
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((template) => ({
              label: <OptionLabel template={template} questionnaires={questionnaires} />,
              value: template.id,
              template,
            }))}
          filterOption={(input, option) => {
            const template = unselectableTemplates.find((t) => t.id === option?.value)
            return template ? template.name.toLowerCase().includes(input.toLowerCase()) : false
          }}
        />

        <span className='text-main-foreground-secondary'>Selected questionnaires</span>
        <div className='flex flex-col gap-2'>
          {selectedTemplates.length > 0 ? (
            selectedTemplates.map((template, index) => {
              return (
                <div
                  key={template.id}
                  className='flex items-center justify-between rounded-sm border px-4 py-2'
                >
                  <OptionLabel template={template} questionnaires={questionnaires} />
                  <XIcon
                    className='size-4 cursor-pointer text-main-foreground-tertiary'
                    onClick={() =>
                      setSelectedTemplates(selectedTemplates.filter((_, i) => i !== index))
                    }
                  />
                </div>
              )
            })
          ) : (
            <span className='text-sm text-main-foreground-secondary'>
              No questionnaires selected yet
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

const OptionLabel = ({
  template,
  questionnaires,
}: {
  template: QuestionnaireTemplate
  questionnaires: Questionnaire[]
}) => {
  const labels: React.ReactNode[] = []
  if (template.isDefault) {
    labels.push('Default')
  }

  if (questionnaires.some((questionnaire) => questionnaire?.base?.name === template.name)) {
    labels.push(
      <TextWithIcon
        text='Already requested'
        icon={<TriangleAlertIcon size={12} />}
        className='text-yellow-500'
      />,
    )
  }

  return (
    <div className='flex flex-col gap-0.5'>
      {template.name}
      <div className='flex flex-wrap items-center gap-1'>
        {labels.map((label, index) => (
          <span key={index} className='flex items-center text-xs text-main-foreground-tertiary'>
            {index > 0 && <span className='mx-1'>|</span>}
            {label}
          </span>
        ))}
      </div>
    </div>
  )
}
